import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { resolveTenantScope, type TenantScope } from "@/lib/tenant-scope.functions";

/**
 * App-wide scope selectors used by operator pages: tenant + facility + customer.
 *
 * SERVER IS AUTHORITATIVE. The tenant a user is operating inside is decided
 * by `resolveTenantScope` (see src/lib/tenant-scope.functions.ts), not by the
 * browser. localStorage is a *hint* only — it is honored only if the server
 * confirms the tenant is visible to this user. Otherwise the server-picked
 * default wins. This prevents a signed-in user from ever rendering another
 * tenant's data, even cosmetically, because of stale local state.
 *
 * The `isSuperAdmin` / `isCrossTenant` flags surfaced here are the ONLY
 * sanctioned client-side super-admin signals. Do not re-derive them from
 * `roles` in `use-auth`.
 */

interface Scope {
  tenantId: string | null;
  facilityId: string | null;
  customerId: string | null; // null = all customers in tenant
  setTenant: (id: string | null) => void;
  setFacility: (id: string | null) => void;
  setCustomer: (id: string | null) => void;
  tenants: { id: string; name: string; config?: unknown }[];
  facilities: { id: string; name: string; tenant_id: string; is_default?: boolean }[];
  customers: { id: string; name: string; tenant_id: string }[];
  loading: boolean;
  pilotMode: boolean;
  /** Server-resolved: true iff this user holds unscoped super_admin/admin. */
  isSuperAdmin: boolean;
  /**
   * Server-resolved: true iff the current effective tenant is being viewed
   * with cross-tenant privileges (i.e. the user is a platform admin AND has
   * no direct role in the active tenant). Drives the persistent banner.
   */
  isCrossTenant: boolean;
}

const ScopeCtx = createContext<Scope | undefined>(undefined);

const KEY = "wms.scope.v1";

function loadStored(): { tenantId: string | null; facilityId: string | null; customerId: string | null } {
  if (typeof window === "undefined") return { tenantId: null, facilityId: null, customerId: null };
  try { return JSON.parse(localStorage.getItem(KEY) ?? "{}"); } catch { return { tenantId: null, facilityId: null, customerId: null }; }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // localStorage is only a HINT. We keep the raw stored tenantId separate
  // from the effective tenantId; the effective one stays null until the
  // server confirms the hint is visible to this user.
  const stored = loadStored();
  const [tenantId, setTenantIdState] = useState<string | null>(null);
  const [facilityId, setFacilityId] = useState<string | null>(stored.facilityId ?? null);
  const [customerId, setCustomerId] = useState<string | null>(stored.customerId ?? null);

  // 1) Server-authoritative scope. Until this resolves, the app renders
  //    a loading state rather than showing any tenant's data.
  const resolveScope = useServerFn(resolveTenantScope);
  const { data: serverScope, isLoading: lsc } = useQuery<TenantScope>({
    queryKey: ["tenant-scope", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: () => resolveScope(),
  });

  const { data: tenants = [], isLoading: lt } = useQuery({
    queryKey: ["scope-tenants", user?.id],
    enabled: !!user && !!serverScope,
    queryFn: async () =>
      (
        await supabase
          .from("tenants")
          .select("id, name, config")
          .order("name")
      ).data ?? [],
  });
  const { data: facilities = [], isLoading: lf } = useQuery({
    queryKey: ["scope-facilities", user?.id],
    enabled: !!user,
    queryFn: async () =>
      (
        await supabase
          .from("facilities")
          .select("id, name, tenant_id, is_default")
          .order("is_default", { ascending: false })
          .order("name")
      ).data ?? [],
  });
  const { data: customers = [], isLoading: lc } = useQuery({
    queryKey: ["scope-customers", user?.id],
    enabled: !!user,
    queryFn: async () => (await supabase.from("customers").select("id, name, tenant_id").order("name")).data ?? [],
  });

  // The worker's assigned (home) facility. Worker Workspace defaults here
  // so a worker never has to pick a warehouse before they can clock in.
  const { data: profile } = useQuery({
    queryKey: ["scope-profile", user?.id],
    enabled: !!user,
    queryFn: async () =>
      (
        await supabase
          .from("profiles")
          .select("default_tenant_id, home_facility_id")
          .eq("id", user!.id)
          .maybeSingle()
      ).data,
  });
  const homeFacilityId = profile?.home_facility_id ?? null;

  // 2) Reconcile localStorage hint with the server-authoritative scope.
  //    - If the hint is in the server's visible set → honor it.
  //    - Otherwise → fall back to server default. Server always wins.
  useEffect(() => {
    if (!serverScope) return;
    const visible = new Set(serverScope.visibleTenantIds);
    const hint = stored.tenantId;
    const next =
      hint && visible.has(hint)
        ? hint
        : serverScope.defaultTenantId;
    // Only update if genuinely different — otherwise React keeps looping
    // on the same value via the localStorage effect below.
    setTenantIdState((prev) => (prev === next ? prev : next));
    // If the hint was rejected, also purge it from storage so we don't
    // keep trying to apply an unauthorized tenant across reloads.
    if (hint && !visible.has(hint) && typeof window !== "undefined") {
      try {
        const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}");
        localStorage.setItem(KEY, JSON.stringify({ ...raw, tenantId: next }));
      } catch {
        /* noop */
      }
    }
    // stored is captured once at provider mount; that's intentional — the
    // effect below keeps localStorage in sync going forward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverScope]);

  // Explicit tenant switch — accept only server-visible tenants.
  const setTenantId = (id: string | null) => {
    if (!serverScope) return;
    if (id && !serverScope.visibleTenantIds.includes(id)) return;
    setTenantIdState(id);
  };

  useEffect(() => {
    const inTenant = facilities.filter((f) => f.tenant_id === tenantId);
    const home = homeFacilityId
      ? inTenant.find((f) => f.id === homeFacilityId)
      : undefined;
    const preferred = home ?? inTenant.find((f) => f.is_default) ?? inTenant[0];
    if (!facilityId && preferred) setFacilityId(preferred.id);
    if (facilityId && !inTenant.find((f) => f.id === facilityId)) setFacilityId(preferred?.id ?? null);
  }, [facilities, tenantId, facilityId, homeFacilityId]);
  useEffect(() => {
    if (customerId) {
      const inTenant = customers.filter((c) => c.tenant_id === tenantId);
      if (!inTenant.find((c) => c.id === customerId)) setCustomerId(null);
    }
  }, [customers, tenantId, customerId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(KEY, JSON.stringify({ tenantId, facilityId, customerId }));
  }, [tenantId, facilityId, customerId]);

  // Intersect the tenants list with the server's visible set so a platform
  // admin still sees every tenant, but a scoped user cannot even see other
  // tenants in the switcher UI.
  const visibleTenants = useMemo(() => {
    if (!serverScope) return [];
    const visible = new Set(serverScope.visibleTenantIds);
    return tenants.filter((t) => visible.has(t.id));
  }, [tenants, serverScope]);

  const pilotMode = useMemo(() => {
    const t = visibleTenants.find((x) => x.id === tenantId);
    const cfg = (t?.config ?? {}) as Record<string, unknown>;
    return cfg.pilot_mode === true;
  }, [visibleTenants, tenantId]);

  const isSuperAdmin = !!serverScope?.isPlatformAdmin;
  const isCrossTenant =
    isSuperAdmin && !!tenantId && !(serverScope?.tenantAdminOf ?? []).includes(tenantId);

  const value = useMemo<Scope>(() => ({
    tenantId, facilityId, customerId,
    setTenant: setTenantId, setFacility: setFacilityId, setCustomer: setCustomerId,
    tenants: visibleTenants, facilities, customers,
    // Loading includes the server scope query — we do NOT render tenant-scoped
    // UI until the server has confirmed which tenants are visible.
    loading: lsc || lt || lf || lc || !serverScope,
    pilotMode,
    isSuperAdmin,
    isCrossTenant,
  }), [tenantId, facilityId, customerId, visibleTenants, facilities, customers, lsc, lt, lf, lc, serverScope, pilotMode, isSuperAdmin, isCrossTenant]);

  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

export function useScope() {
  const ctx = useContext(ScopeCtx);
  if (!ctx) throw new Error("useScope must be used within ScopeProvider");
  return ctx;
}